/* eslint-disable no-restricted-syntax */
/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import { isWaitlistMode } from "@/lib/utils/waitlist-mode";
import { ArrowRight } from "lucide-react";
import { FadeIn } from "./primitives/fade-in";
import { BrowserChrome, AnnotationCallout } from "./primitives";

interface HeroSectionProps {
  onGetStarted: () => void;
  onWatchDemo: () => void;
}

export function HeroSection({ onGetStarted, onWatchDemo }: HeroSectionProps) {
  return (
    <section
      className="relative min-h-screen flex items-center bg-background-warm overflow-hidden"
      aria-label="Hero"
    >
      {/* Gold blueprint grid background */}
      <div
        className="blueprint-grid-gold absolute inset-0 pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 md:px-8 lg:px-20 pt-28 md:pt-36 pb-16 md:pb-24 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — Copy */}
          <div className="lg:col-span-7">
            <FadeIn>
              <span className="section-badge mb-4">
                Now Available — Early Access Pricing
              </span>
            </FadeIn>

            <FadeIn delay={100}>
              <h1 className="mb-4 md:mb-5 text-[clamp(36px,6.5vw,72px)] leading-[0.95] tracking-[-0.04em] font-bold">
                <span className="block text-foreground">
                  Your whole business.
                </span>
                <span className="block text-foreground">
                  One <span className="text-highlight">simple</span> platform.
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={200}>
              <p className="mb-6 text-muted-foreground max-w-lg text-[clamp(16px,1.5vw,19px)] leading-[1.5] font-normal">
                Estimates, proposals, projects, costs, invoices — connected.
                Stop switching between spreadsheets and apps. Start running your
                business from one place.
              </p>
            </FadeIn>

            <FadeIn delay={300}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 mb-6 md:mb-8">
                <Button
                  size="pill-lg"
                  onClick={onGetStarted}
                  className="bg-secondary hover:bg-secondary/90 text-white font-medium shadow-lg hover:shadow-xl transition-all hover-lift"
                >
                  {isWaitlistMode() ? "Join the Waitlist" : "Get Started"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <Button
                  variant="outline"
                  size="pill-lg"
                  onClick={onWatchDemo}
                  className="border-muted-foreground/30 hover:bg-muted/50 font-medium"
                >
                  See How It Works
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={400}>
              <ul className="flex flex-wrap items-center gap-6 text-xs text-muted-foreground uppercase tracking-wider list-none m-0 p-0">
                <li>Plans from $29/mo</li>
                <li className="text-border" aria-hidden="true">
                  •
                </li>
                <li>No long-term contracts</li>
                <li className="text-border" aria-hidden="true">
                  •
                </li>
                <li>Cancel anytime</li>
              </ul>
            </FadeIn>
          </div>

          {/* Right — Product Visual */}
          <FadeIn className="lg:col-span-5 relative" delay={200}>
            {/* Blueprint corner marks */}
            <svg
              className="absolute -top-3 -left-3 w-6 h-6 text-secondary/30"
              aria-hidden="true"
            >
              <path
                d="M0 20V0h20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            <svg
              className="absolute -bottom-3 -right-3 w-6 h-6 text-secondary/30"
              aria-hidden="true"
            >
              <path
                d="M24 4V24H4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>

            <BrowserChrome>
              {/* JSX estimate UI mock */}
              <div className="p-4 bg-background text-xs">
                {/* Header */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
                  <div>
                    <div className="font-semibold text-sm text-foreground">
                      Estimate #1042
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      Commercial HVAC — Phase 2
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    Ready
                  </span>
                </div>

                {/* Line items */}
                <div className="space-y-1.5">
                  {[
                    {
                      item: "Copper Pipe 3/4\u2033",
                      qty: "240 LF",
                      cost: "$1,440",
                    },
                    {
                      item: "Duct Insulation R-8",
                      qty: "180 SF",
                      cost: "$864",
                    },
                    {
                      item: "Journeyman Labor",
                      qty: "32 hrs",
                      cost: "$2,240",
                    },
                    { item: "Crane Rental", qty: "2 days", cost: "$1,800" },
                  ].map((row) => (
                    <div
                      key={row.item}
                      className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30"
                    >
                      <span className="text-foreground font-medium truncate max-w-[140px]">
                        {row.item}
                      </span>
                      <div className="flex items-center gap-4 text-muted-foreground">
                        <span className="w-14 text-right">{row.qty}</span>
                        <span className="w-16 text-right font-medium text-foreground">
                          {row.cost}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="mt-3 pt-2 border-t border-border/40 flex justify-between font-semibold text-sm">
                  <span>Total</span>
                  <span className="text-foreground">$6,344.00</span>
                </div>
              </div>
            </BrowserChrome>

            {/* Floating annotations */}
            <FadeIn delay={700}>
              <AnnotationCallout
                label="Material cost:"
                value="$2,304"
                className="-bottom-4 -left-4 md:-left-8"
              />
            </FadeIn>

            <FadeIn delay={900}>
              <AnnotationCallout
                label="Proposal ready:"
                value="minutes"
                className="-top-4 -right-2 md:-right-6"
              />
            </FadeIn>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
